/* eslint-disable no-undef */
import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { getRandomApiKey } from './config';

// 计算颜色亮度（0-255）
const getLuminance = (hexColor) => {
  // 移除 '#' 符号（如果有）
  const hex = hexColor.replace('#', '');
  
  // 解析RGB值
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // 根据人眼感知计算亮度 (标准亮度计算公式)
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

// 根据背景色亮度选择适合的文字颜色
const getContrastColor = (bgColor) => {
  const luminance = getLuminance(bgColor);
  return luminance > 128 ? '#000000' : '#FFFFFF';
};

function App() {
  // State variables for form inputs
  const [stockCode, setStockCode] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const tenYearsAgo = new Date(today.setFullYear(today.getFullYear() - 10));
    return tenYearsAgo.toISOString().split('T')[0];
  });
  const [initialAmount, setInitialAmount] = useState(10000);
  const [bgColor, setBgColor] = useState('#f0f8ff');
  const [curveColor, setCurveColor] = useState('#FF5733');
  const [curveThickness, setCurveThickness] = useState(4);
  const [textColor, setTextColor] = useState('#000000'); // 默认文字颜色
  
  // 当背景色变化时，自动调整文字颜色
  useEffect(() => {
    setTextColor(getContrastColor(bgColor));
  }, [bgColor]);
  
  // State variables for application state
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [stockData, setStockData] = useState(null);
  const [resultVisible, setResultVisible] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  
  // Refs
  const canvasRef = useRef(null);
  const captureRef = useRef(null);
  
  // Check if required libraries are available
  useEffect(() => {
    const checkLibraries = () => {
      const messages = [];
      
      if (typeof CCapture === 'undefined') {
        console.error('CCapture library is not loaded');
        messages.push('视频捕获组件(CCapture)加载失败');
      }
      
      if (typeof FFmpeg === 'undefined') {
        console.error('FFmpeg library is not loaded');
        messages.push('视频转换组件(FFmpeg)加载失败');
      }
      
      if (messages.length > 0) {
        setErrorMessage(`${messages.join('，')}。请检查您的网络连接并刷新页面，或尝试使用不同的浏览器。`);
      }
    };
    
    // Check libraries after a short delay to ensure they have time to load
    const timer = setTimeout(checkLibraries, 1000);
    return () => clearTimeout(timer);
  }, []);
  
  // Function to fetch stock data from Alpha Vantage API
  const fetchStockData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      const apiKey = getRandomApiKey();
      if (!apiKey) {
        throw new Error('无法获取API密钥，请联系管理员');
      }
      
      const url = `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY&symbol=${stockCode}&outputsize=full&apikey=${apiKey}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data['Error Message']) {
        throw new Error(`获取股票数据失败: ${data['Error Message']}`);
      }
      
      if (!data['Time Series (Daily)']) {
        throw new Error('获取股票数据失败，请检查股票代码是否正确');
      }
      
      // Process the data
      const timeSeriesData = data['Time Series (Daily)'];
      const allData = Object.keys(timeSeriesData)
        .filter(date => date >= startDate)
        .sort()
        .map(date => ({
          date,
          close: parseFloat(timeSeriesData[date]['4. close'])
        }));
      
      if (allData.length === 0) {
        throw new Error('选定日期范围内无股票数据');
      }
      
      // 应用智能采样 - 根据数据量调整采样间隔
      const processedData = sampleData(allData);
      
      // Calculate total amount
      const initialPrice = processedData[0].close;
      const currentPrice = processedData[processedData.length - 1].close;
      const shares = initialAmount / initialPrice;
      const calculatedTotalAmount = shares * currentPrice;
      
      setStockData(processedData);
      setTotalAmount(calculatedTotalAmount);
      setResultVisible(true);
      
      // Check if CCapture is available
      if (typeof CCapture !== 'undefined') {
        // Generate the animation after data is fetched
        setTimeout(() => {
          try {
            drawAnimatedChart(processedData, initialPrice);
          } catch (error) {
            console.error("Failed to generate animated chart:", error);
            generateStaticImage(processedData, initialPrice);
          }
        }, 100);
      } else {
        // Fallback to static image if CCapture is not available
        console.warn("CCapture not available, falling back to static image");
        generateStaticImage(processedData, initialPrice);
      }
      
    } catch (error) {
      setErrorMessage(error.message);
      console.error('Error fetching stock data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 智能采样函数 - 根据数据量动态调整采样间隔
  const sampleData = (data) => {
    // 保留第一个和最后一个数据点
    if (data.length <= 2) return data;
    
    // 目标数据点数 - 基于期望的视频长度(30秒)和动画速度
    const targetDataPoints = 180; // 理想情况下，每个数据点需要约1/6秒显示
    
    // 计算合适的采样间隔
    let interval = Math.max(1, Math.floor(data.length / targetDataPoints));
    
    // 若数据量极大，进一步增加间隔
    if (data.length > 1000) {
      interval = Math.max(15, Math.floor(data.length / 180));
    } else if (data.length > 500) {
      interval = Math.max(8, Math.floor(data.length / 180));
    } else if (data.length > 250) {
      interval = Math.max(5, Math.floor(data.length / 180));
    } else {
      // 数据较少时，保证至少有一定数量的采样点
      interval = Math.max(1, Math.min(interval, Math.floor(data.length / 60)));
    }
    
    console.log(`数据总量: ${data.length}, 采样间隔: ${interval}`);
    
    // 执行采样
    const sampledData = [];
    sampledData.push(data[0]); // 始终包含第一个点
    
    for (let i = interval; i < data.length - 1; i += interval) {
      sampledData.push(data[i]);
    }
    
    // 确保最后一个点被包含
    if (sampledData[sampledData.length - 1] !== data[data.length - 1]) {
      sampledData.push(data[data.length - 1]);
    }
    
    console.log(`采样后数据量: ${sampledData.length}`);
    
    // 设置采样信息，用于用户界面显示
    setErrorMessage(`正在进行数据采样: 原始数据 ${data.length} 个交易日，采样后 ${sampledData.length} 个数据点（间隔 ${interval}）。即将开始生成视频...`);
    
    return sampledData;
  };
  
  // Function to draw the animated chart
  const drawAnimatedChart = (data, initialPrice) => {
    if (!canvasRef.current || !data || data.length === 0) return;
    
    const canvas = canvasRef.current;
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext('2d');
    
    // Calculate shares and values
    const shares = initialAmount / initialPrice;
    const values = data.map(item => item.close * shares);
    
    // Find min and max values for scaling, ensure the minimum is no less than 0
    const minValue = Math.max(0, Math.min(...values) * 0.95);
    const maxValue = Math.max(...values) * 1.05;
    
    // Setup capture
    try {
      // Check if CCapture is defined
      if (typeof CCapture === 'undefined') {
        throw new Error('CCapture 库未加载，请检查您的网络连接并刷新页面。');
      }
      
      // Use the global CCapture instead of window.CCapture
      captureRef.current = new CCapture({
        format: 'webm',
        framerate: 24, // 保持24帧率以确保平滑播放
        verbose: true
      });
      
      // Animation variables
      let frameCount = 0;
      // 根据采样后的数据量动态计算总帧数，目标是30秒视频
      const targetSeconds = 30;
      const framesPerSecond = 24;
      const pointsPerSecond = data.length / targetSeconds;
      const totalFrames = Math.min(targetSeconds * framesPerSecond, Math.ceil(data.length * (framesPerSecond / pointsPerSecond)));
      
      console.log(`数据点数: ${data.length}, 目标时长: ${targetSeconds}秒, 总帧数: ${totalFrames}`);
      
      // Start capture
      captureRef.current.start();
      
      // Animation function
      const animate = () => {
        // Clear canvas
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // 计算当前帧要显示的数据点数量（使用线性进度，完全匀速显示）
        const progress = frameCount / totalFrames; // 纯线性进度，完全匀速绘制
        const pointsToDraw = Math.min(Math.ceil(progress * data.length), data.length);
        
        if (pointsToDraw > 0) {
          // 提取当前要绘制的数据
          const currentFrameData = data.slice(0, pointsToDraw);
          
          // 先绘制坐标系 - 它会根据新的逻辑自动处理从左到右填充和滚动
          drawCoordinateSystem(ctx, canvas, currentFrameData, minValue, maxValue);
          
          // 绘制曲线 - 使用新的从左到右填充和滚动逻辑
          drawCurve(ctx, currentFrameData, canvas, minValue, maxValue);
          
          // 绘制当前值信息 - 始终显示最新的点
          const currentValue = values[pointsToDraw - 1];
          const currentDate = data[pointsToDraw - 1].date;
          drawCurrentValue(ctx, canvas, currentValue, currentDate);
        }
        
        // Capture frame
        captureRef.current.capture(canvas);
        
        // Continue animation if not finished
        if (frameCount < totalFrames) {
          frameCount++;
          requestAnimationFrame(animate);
        } else {
          // End capture and generate video
          captureRef.current.stop();
          captureRef.current.save(blob => {
            processVideo(blob);
          });
        }
      };
      
      // Start animation
      animate();
    } catch (error) {
      console.error("Error initializing CCapture:", error);
      setErrorMessage("无法初始化视频捕获组件: " + error.message);
      setIsLoading(false);
    }
  };
  
  // Function to draw the coordinate system
  const drawCoordinateSystem = (ctx, canvas, data, minValue, maxValue) => {
    // 为上方信息面板预留空间并为底部日期标签增加空间（135度显示完整年月日需要的空间）
    const padding = { left: 100, right: 100, top: 160, bottom: 200 }; // 恢复到适合单行文本的底部间距
    const graphWidth = canvas.width - padding.left - padding.right;
    const graphHeight = canvas.height - padding.top - padding.bottom;
    
    // 使用固定宽度1200像素，而不是4/5比例
    const fixedWidth = 1200;
    
    // 计算理想的固定点间距
    const idealPointSpacing = 10;
    
    // 计算基于固定宽度可显示的最大点数
    const maxVisiblePoints = Math.floor(fixedWidth / idealPointSpacing);
    
    // 统一使用相同的点间距 - 基于固定宽度
    const fixedSpacing = fixedWidth / (maxVisiblePoints - 1 || 1);
    
    // 根据数据点数量决定显示方式
    let visibleData;
    let startX;
    
    if (data.length <= maxVisiblePoints) {
      // 数据点较少，从左向右填充，不需要滚动
      visibleData = data;
      startX = padding.left;
    } else {
      // 数据点超过阈值，需要滚动显示
      visibleData = data.slice(data.length - maxVisiblePoints);
      startX = padding.left;
    }
    
    // Draw axes
    ctx.strokeStyle = textColor; // 使用动态文字颜色
    ctx.lineWidth = 2;
    ctx.beginPath();
    
    // X-axis
    ctx.moveTo(padding.left, canvas.height - padding.bottom);
    ctx.lineTo(canvas.width - padding.right, canvas.height - padding.bottom);
    
    // Y-axis
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, canvas.height - padding.bottom);
    ctx.stroke();
    
    // Draw grid lines and labels
    ctx.strokeStyle = textColor === '#FFFFFF' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'; // 网格线使用半透明的文字颜色
    ctx.lineWidth = 1;
    ctx.fillStyle = textColor; // 使用动态文字颜色
    ctx.font = '20px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    
    // Y-axis grid and labels (5 levels)
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (graphHeight * (5 - i) / 5);
      const value = minValue + ((maxValue - minValue) * i / 5);
      
      // Grid line
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(canvas.width - padding.right, y);
      ctx.stroke();
      
      // Label
      ctx.fillText(
        value.toLocaleString('zh-CN', { maximumFractionDigits: 0 }),
        padding.left - 10,
        y
      );
    }
    
    // X-axis labels (date milestones) - 使用可见数据
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    // 保存上下文状态
    ctx.save();
    
    // 根据数据点数量计算应该显示的日期标签间隔，避免重叠
    const minSpaceBetweenLabels = 50; // 减少为50，原来是100，增加日期标签密度
    const skipInterval = Math.max(1, Math.ceil(minSpaceBetweenLabels / fixedSpacing));
    
    // 为每个数据点绘制日期标签（但是根据间隔跳过一些，避免重叠）
    for (let i = 0; i < visibleData.length; i++) {
      // 只在开始、结束和每隔skipInterval的位置显示标签
      if (i === 0 || i === visibleData.length - 1 || i % skipInterval === 0) {
        const datePoint = visibleData[i];
        const date = new Date(datePoint.date);
        const x = startX + (fixedSpacing * i);
        
        // 只有当x坐标在显示范围内时才绘制
        if (x >= padding.left && x <= canvas.width - padding.right) {
          // 移动到标签位置
          ctx.translate(x, canvas.height - padding.bottom + 15);
          
          // 逆时针旋转135度再旋转180度，等于顺时针旋转45度（7π/4弧度）
          ctx.rotate(Math.PI * 7/4);
          
          // 格式化日期为完整的年月日（确保月和日为两位数）
          const year = date.getFullYear();
          const month = date.getMonth() + 1;
          const day = date.getDate();
          // 格式化月和日为两位数
          const monthStr = month < 10 ? `0${month}` : `${month}`;
          const dayStr = day < 10 ? `0${day}` : `${day}`;
          // 恢复为单行格式
          const formattedDate = `${year}年${monthStr}月${dayStr}日`;
          
          // 绘制文本（无背景）
          ctx.fillStyle = textColor;
          ctx.font = 'bold 16px Arial'; // 增大字体以提高可读性
          ctx.textAlign = 'right'; // 旋转180度后需要改为right对齐
          ctx.textBaseline = 'bottom'; // 旋转180度后需要改为bottom基线
          
          // 使用单行文本，稍微调整位置
          ctx.fillText(formattedDate, 0, -4);
          
          // 重置变换，准备绘制下一个标签
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          
          // 绘制刻度线
          ctx.beginPath();
          ctx.strokeStyle = textColor;
          ctx.lineWidth = 1;
          ctx.moveTo(x, canvas.height - padding.bottom);
          ctx.lineTo(x, canvas.height - padding.bottom + 8);
          ctx.stroke();
        }
      }
    }
    
    // 恢复上下文状态
    ctx.restore();
  };
  
  // Function to draw the curve
  const drawCurve = (ctx, data, canvas, minValue, maxValue) => {
    if (!data || data.length === 0) return;
    
    // 与坐标系相同的边距设置
    const padding = { left: 100, right: 100, top: 160, bottom: 200 }; // 确保与坐标系的padding一致
    const graphWidth = canvas.width - padding.left - padding.right;
    const graphHeight = canvas.height - padding.top - padding.bottom;
    
    // 计算股票份额
    const initialPrice = data[0].close;
    const shares = initialAmount / initialPrice;
    
    // 设置曲线样式
    ctx.strokeStyle = curveColor;
    ctx.lineWidth = curveThickness;
    
    // 使用固定宽度1200像素，而不是4/5比例
    const fixedWidth = 1200;
    
    // 计算理想的固定点间距
    const idealPointSpacing = 10; // 理想的点之间的像素距离
    
    // 计算基于固定宽度可显示的最大点数
    const maxVisiblePoints = Math.floor(fixedWidth / idealPointSpacing);
    
    // 统一使用相同的点间距 - 基于固定宽度
    const fixedSpacing = fixedWidth / (maxVisiblePoints - 1 || 1);
    
    // 根据数据点数量决定显示方式
    let visibleData;
    let startX;
    
    if (data.length <= maxVisiblePoints) {
      // 数据点较少，从左向右填充，不需要滚动
      visibleData = data;
      startX = padding.left;
    } else {
      // 数据点超过阈值，需要滚动显示
      visibleData = data.slice(data.length - maxVisiblePoints);
      startX = padding.left;
    }
    
    // 绘制曲线
    ctx.beginPath();
    
    // 确保至少有两个点才能绘制曲线
    if (visibleData.length > 1) {
      // 将股价数据转换为坐标点
      const points = visibleData.map((point, index) => {
        const value = point.close * shares;
        const x = startX + (fixedSpacing * index);
        // 确保值不小于minValue，防止曲线低于X轴
        const clampedValue = Math.max(minValue, value);
        const y = padding.top + graphHeight - (graphHeight * (clampedValue - minValue) / (maxValue - minValue || 1));
        // 确保y坐标不超出图表范围
        return { 
          x, 
          y: Math.max(padding.top, Math.min(y, padding.top + graphHeight))
        };
      });
      
      // 使用基数样条算法绘制平滑曲线
      ctx.moveTo(points[0].x, points[0].y);
      
      // 张力系数 - 控制曲线的平滑度，值越小越平滑
      const tension = 0.3;
      
      for (let i = 0; i < points.length - 1; i++) {
        // 获取当前点和下一个点
        const p0 = i > 0 ? points[i - 1] : points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = i < points.length - 2 ? points[i + 2] : p2;
        
        // 计算控制点
        const cp1x = p1.x + (p2.x - p0.x) * tension;
        const cp1y = p1.y + (p2.y - p0.y) * tension;
        const cp2x = p2.x - (p3.x - p1.x) * tension;
        const cp2y = p2.y - (p3.y - p1.y) * tension;
        
        // 使用贝塞尔曲线连接点
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
      }
    } else if (visibleData.length === 1) {
      // 只有一个点时，绘制一个点
      const point = visibleData[0];
      const value = point.close * shares;
      const x = startX;
      const y = padding.top + graphHeight - (graphHeight * (value - minValue) / (maxValue - minValue || 1));
      // 确保y坐标不超出图表范围
      const clampedY = Math.max(padding.top, Math.min(y, padding.top + graphHeight));
      ctx.moveTo(x, clampedY);
      ctx.lineTo(x, clampedY);
    }
    
    ctx.stroke();
    
    // 绘制数据点 - 只显示最新的点
    drawDataPoints(ctx, visibleData, shares, startX, fixedSpacing, graphHeight, minValue, maxValue, padding, data.length > maxVisiblePoints);
  };
  
  // 辅助函数：绘制数据点
  const drawDataPoints = (ctx, data, shares, startX, spacing, graphHeight, minValue, maxValue, padding, isScrolled = false) => {
    ctx.fillStyle = curveColor;
    
    // 只绘制最新的数据点（最后一个点）
    if (data.length > 0) {
      const lastPoint = data[data.length - 1];
      const value = lastPoint.close * shares;
      
      // X坐标基于固定间距
      const x = startX + (spacing * (data.length - 1));
      // 确保值不小于minValue，防止点低于X轴
      const clampedValue = Math.max(minValue, value);
      const y = padding.top + graphHeight - (graphHeight * (clampedValue - minValue) / (maxValue - minValue || 1));
      // 确保y坐标不超出图表范围
      const clampedY = Math.max(padding.top, Math.min(y, padding.top + graphHeight));
      
      // 绘制稍大一点的圆点
      ctx.beginPath();
      ctx.arc(x, clampedY, 7, 0, Math.PI * 2); // 增大圆点半径
      ctx.fill();
    }
  };
  
  // Function to draw the current value
  const drawCurrentValue = (ctx, canvas, currentValue, currentDate) => {
    // 计算收益率和变化文本
    const change = ((currentValue - initialAmount) / initialAmount * 100).toFixed(2);
    const changeText = change >= 0 ? `+${change}%` : `${change}%`;
    
    // 确定收益率颜色
    const valueColor = currentValue > initialAmount ? 
      (textColor === '#FFFFFF' ? '#33FF57' : '#00CC00') : 
      (textColor === '#FFFFFF' ? '#FF5733' : '#CC0000');
      
    // 设置顶部信息面板区域（占据整个顶部）
    const topMargin = 30; // 增加顶部边距，使标题栏更高一些
    const panelHeight = 120;
    const panelBgColor = textColor === '#FFFFFF' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.85)';
    
    // 绘制顶部半透明背景面板（整个宽度）
    ctx.fillStyle = panelBgColor;
    ctx.strokeStyle = curveColor;
    ctx.lineWidth = 2;
    roundRect(ctx, 20, topMargin, canvas.width - 40, panelHeight, 15, true, true);
    
    // 在面板内创建三列布局
    const colWidth = (canvas.width - 40 - 60) / 3; // 减去面板边距和列间隔
    const col1X = 50;
    const col2X = col1X + colWidth + 30;
    const col3X = col2X + colWidth + 30;
    const labelY = topMargin + 35;
    const valueY = topMargin + 80;
    
    // 设置标签文字样式
    ctx.font = 'bold 20px Arial';
    ctx.fillStyle = textColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // 绘制标题标签
    ctx.fillText('初始投资', col1X + colWidth/2, labelY);
    ctx.fillText('当前价值', col2X + colWidth/2, labelY);
    ctx.fillText('当前日期', col3X + colWidth/2, labelY);
    
    // 绘制初始投资额
    ctx.font = 'bold 28px Arial';
    ctx.fillText(
      `${initialAmount.toLocaleString('zh-CN')} 元`,
      col1X + colWidth/2,
      valueY
    );
    
    // 绘制当前价值（现在在第二列）- 大幅增大字体并将收益率放在括号内
    ctx.fillStyle = valueColor;
    ctx.font = 'bold 48px Arial'; // 大幅增大字体
    ctx.fillText(
      `${currentValue.toLocaleString('zh-CN', { maximumFractionDigits: 0 })} 元 (${changeText})`,
      col2X + colWidth/2,
      valueY
    );
    
    // 绘制当前日期 - 移至第三列
    ctx.fillStyle = textColor;
    ctx.font = 'bold 28px Arial';
    ctx.fillText(
      currentDate,
      col3X + colWidth/2,
      valueY
    );
    
    // 绘制标题栏（半透明黑条）
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    roundRect(ctx, 0, 0, canvas.width, topMargin + 5, 0, true, false);
    
    // 绘制股票标题 - 调整垂直位置
    ctx.font = 'bold 22px Arial';
    ctx.fillStyle = '#FFFFFF'; // 白色，确保在深色背景上可见
    ctx.textAlign = 'center';
    ctx.fillText(
      `${stockCode} 资金曲线 (${startDate} 至今)`,
      canvas.width / 2,
      topMargin / 2 + 2
    );
  };
  
  // 辅助函数：绘制圆角矩形
  const roundRect = (ctx, x, y, width, height, radius, fill, stroke) => {
    if (typeof radius === 'number') {
      radius = {tl: radius, tr: radius, br: radius, bl: radius};
    } else {
      const defaultRadius = {tl: 0, tr: 0, br: 0, bl: 0};
      radius = {...defaultRadius, ...radius};
    }
    
    ctx.beginPath();
    ctx.moveTo(x + radius.tl, y);
    ctx.lineTo(x + width - radius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx.lineTo(x + width, y + height - radius.br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
    ctx.lineTo(x + radius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx.lineTo(x, y + radius.tl);
    ctx.quadraticCurveTo(x, y, x + radius.tl, y);
    ctx.closePath();
    
    if (fill) {
      ctx.fill();
    }
    if (stroke) {
      ctx.stroke();
    }
  };
  
  // Function to process video using FFmpeg
  const processVideo = async (webmBlob) => {
    try {
      setIsLoading(true);
      setErrorMessage("正在将视频转换为MP4格式，请稍候...");
      
      // Create a temporary URL for the webm blob
      const webmUrl = URL.createObjectURL(webmBlob);
      
      // Use FFmpeg.js to convert WebM to MP4
      if (typeof FFmpeg === 'undefined') {
        throw new Error('FFmpeg 库未加载，无法转换为MP4格式');
      }
      
      try {
        // Create FFmpeg instance
        const ffmpeg = FFmpeg.createFFmpeg({ 
          log: true,
          corePath: 'https://unpkg.com/@ffmpeg/core@0.10.0/dist/ffmpeg-core.js'
        });
        
        // Load FFmpeg
        await ffmpeg.load();
        
        // Convert webm blob to arrayBuffer
        const webmBuffer = await webmBlob.arrayBuffer();
        
        // Write the webm file to FFmpeg's virtual file system
        ffmpeg.FS('writeFile', 'input.webm', new Uint8Array(webmBuffer));
        
        // Run FFmpeg command to convert webm to mp4 with higher quality settings
        // 使用更高的视频比特率和更好的预设，以提高视频质量
        await ffmpeg.run(
          '-i', 'input.webm', 
          '-c:v', 'libx264', 
          '-preset', 'veryslow', // 使用"veryslow"预设获得更好的压缩效率和视频质量
          '-crf', '17', // 更低的CRF值意味着更高的质量（范围17-28，17为接近无损）
          '-b:v', '4M', // 增加视频比特率
          '-maxrate', '5M', 
          '-bufsize', '8M',
          '-c:a', 'aac', 
          '-b:a', '320k', // 提高音频质量
          'output.mp4'
        );
        
        // Read the output mp4 file
        const mp4Data = ffmpeg.FS('readFile', 'output.mp4');
        
        // Create blob from the mp4 data
        const mp4Blob = new Blob([mp4Data.buffer], { type: 'video/mp4' });
        
        // Create URL for the mp4 blob
        const mp4Url = URL.createObjectURL(mp4Blob);
        
        // Update the video URL
        setVideoUrl(mp4Url);
        
        // Clean up temporary webm URL
        URL.revokeObjectURL(webmUrl);
        
        // 明确清除错误消息，确保成功后不显示任何错误
        setErrorMessage('');
        console.log("MP4 conversion successful");
      } catch (ffmpegError) {
        console.error('FFmpeg processing error:', ffmpegError);
        throw new Error(`视频转换失败: ${ffmpegError.message}`);
      } finally {
        // 确保加载状态在任何情况下都被重置
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error processing video:', error);
      
      // Fall back to webm if mp4 conversion fails
      if (webmBlob) {
        console.warn('Falling back to WebM format');
        const webmUrl = URL.createObjectURL(webmBlob);
        setVideoUrl(webmUrl);
        setErrorMessage('MP4转换失败，已生成WebM格式视频');
      } else {
        setErrorMessage('视频处理失败: ' + error.message);
      }
      
      setIsLoading(false);
    }
  };
  
  // Fallback function to generate a static image if video capture fails
  const generateStaticImage = (data, initialPrice) => {
    if (!canvasRef.current || !data || data.length === 0) return;
    
    // 注意：data 已经是采样后的数据，不需要再次采样
    const canvas = canvasRef.current;
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext('2d');
    
    // Calculate shares and values
    const shares = initialAmount / initialPrice;
    const values = data.map(item => item.close * shares);
    
    // Find min and max values for scaling, ensure the minimum is no less than 0
    const minValue = Math.max(0, Math.min(...values) * 0.95);
    const maxValue = Math.max(...values) * 1.05;
    
    // Clear canvas with background color
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 绘制顺序：先绘制坐标系和曲线，最后绘制信息面板
    // 坐标系使用45度角倾斜的完整中文年月日格式标签，避免重叠
    // 曲线从左到右填充，达到1200像素宽度时开始滚动
    drawCoordinateSystem(ctx, canvas, data, minValue, maxValue);
    drawCurve(ctx, data, canvas, minValue, maxValue);
    
    // 确定信息面板数据 - 始终显示最新的数据点
    const currentValue = values[values.length - 1];
    const currentDate = data[data.length - 1].date;
    drawCurrentValue(ctx, canvas, currentValue, currentDate);
    
    // 添加水印以标识这是静态图像
    ctx.font = '16px Arial';
    ctx.fillStyle = textColor === '#FFFFFF' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('Generated with Stock Curve Generator', canvas.width - 20, canvas.height - 20);
    
    setIsLoading(false);
    setErrorMessage('视频生成失败，已生成静态图表，您可以使用下方按钮下载图表。');
  };
  
  // Function to download canvas as image
  const downloadCanvas = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `${stockCode}_price_curve.png`;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // 验证初始投资金额
    if (!initialAmount || initialAmount <= 0) {
      setErrorMessage('请输入有效的初始投资金额（必须大于0）');
      return;
    }
    
    // 验证股票代码
    if (!stockCode.trim()) {
      setErrorMessage('请输入有效的股票代码');
      return;
    }
    
    // 清除之前的错误信息
    setErrorMessage('');
    
    // 继续获取股票数据
    fetchStockData();
  };
  
  // Function to handle video download
  const handleDownload = () => {
    if (videoUrl) {
      const a = document.createElement('a');
      a.href = videoUrl;
      a.download = `${stockCode}_price_curve.mp4`; // Changed to mp4 extension
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (canvasRef.current) {
      // Fallback to static image download
      downloadCanvas();
    }
  };
  
  // 格式化金额显示
  const formatCurrency = (amount) => {
    return amount.toLocaleString('zh-CN');
  };
  
  // Update the maximum date to today
  const maxDate = new Date().toISOString().split('T')[0];
  
  return (
    <div className="App">
      <header className="App-header">
        <h1 className="App-title">股票资金曲线生成器</h1>
        <p className="App-subtitle">生成股票投资资金曲线视频，展示您的投资收益</p>
      </header>
      
      <form onSubmit={handleSubmit}>
        <div className="form-container">
          <div className="input-container">
            <div className="input-group">
              <label htmlFor="stock-code" className="input-label">股票代码</label>
              <input
                id="stock-code"
                type="text"
                className="input-field"
                value={stockCode}
                onChange={(e) => setStockCode(e.target.value.toUpperCase())}
                placeholder="例如: AAPL, MSFT, GOOG"
                required
              />
              <div className="hint-text">输入美股代码，如 AAPL 表示苹果公司</div>
            </div>
            
            <div className="input-group">
              <label htmlFor="start-date" className="input-label">起始日期</label>
              <input
                id="start-date"
                type="date"
                className="input-field"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={maxDate}
                required
              />
              <div className="hint-text">选择投资起始日期</div>
            </div>
            
            <div className="input-group">
              <label htmlFor="initial-amount" className="input-label">初始投资金额 (元)</label>
              <input
                id="initial-amount"
                type="text"
                className="input-field"
                value={initialAmount}
                onChange={(e) => {
                  // 移除所有非数字字符（除了小数点）
                  const cleanedValue = e.target.value.replace(/[^\d.]/g, '');
                  // 如果是空字符串，设置为0
                  const value = cleanedValue === '' ? 0 : parseFloat(cleanedValue);
                  // 如果是有效数字，更新状态
                  if (!isNaN(value)) {
                    setInitialAmount(value);
                  }
                }}
                placeholder="例如: 10000"
                required
              />
              <div className="hint-text">
                当前设置: {formatCurrency(initialAmount)} 元 | 输入任意正数金额
              </div>
            </div>
          </div>
          
          <div className="advanced-options">
            <details>
              <summary>高级选项</summary>
              <div className="color-input-container">
                <div className="color-input-group">
                  <label htmlFor="bg-color" className="input-label">背景色</label>
                  <input
                    id="bg-color"
                    type="color"
                    className="color-picker"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                  />
                  <div 
                    className="color-preview" 
                    style={{ 
                      backgroundColor: bgColor, 
                      color: textColor,
                      padding: '3px 6px',
                      borderRadius: '3px',
                      fontSize: '12px',
                      marginTop: '5px'
                    }}
                  >
                    文字颜色会自动调整
                  </div>
                </div>
                
                <div className="color-input-group">
                  <label htmlFor="curve-color" className="input-label">曲线颜色</label>
                  <input
                    id="curve-color"
                    type="color"
                    className="color-picker"
                    value={curveColor}
                    onChange={(e) => setCurveColor(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="range-input-container">
                <label htmlFor="curve-thickness" className="input-label">曲线粗细（{curveThickness}像素）</label>
                <input
                  id="curve-thickness"
                  type="range"
                  min="1"
                  max="10"
                  value={curveThickness}
                  onChange={(e) => setCurveThickness(Number(e.target.value))}
                  className="input-field"
                />
              </div>
            </details>
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              className="generate-button"
              disabled={isLoading}
              style={{ flex: '3' }}
            >
              {isLoading ? '正在生成...' : '生成高清MP4视频'}
            </button>
            
            <button
              type="button"
              onClick={() => {
                setStockCode('');
                setInitialAmount(10000);
                const today = new Date();
                const tenYearsAgo = new Date(today.setFullYear(today.getFullYear() - 10));
                setStartDate(tenYearsAgo.toISOString().split('T')[0]);
              }}
              className="generate-button"
              disabled={isLoading}
              style={{ 
                flex: '1', 
                backgroundColor: '#6c757d'
              }}
            >
              重置
            </button>
          </div>
        </div>
      </form>
      
      {errorMessage && (
        <div className="error-message">
          <p>{errorMessage}</p>
        </div>
      )}
      
      <div className="canvas-container">
        <canvas ref={canvasRef} className="stock-canvas"></canvas>
        {resultVisible && stockData && !videoUrl && (
          <button onClick={downloadCanvas} className="download-button">
            下载图表
          </button>
        )}
      </div>
      
      {resultVisible && stockData && (
        <div className="result-container">
          <p className="result-text">
            从 {startDate} 以 {initialAmount.toLocaleString('zh-CN')} 元买入 {stockCode}，
            现在总金额为 
            <span className={totalAmount >= initialAmount ? 'positive-amount' : 'negative-amount'}>
              {' '}{totalAmount.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}{' '}
            </span>
            元
          </p>
          
          {videoUrl && (
            <div className="video-result">
              <h3>MP4视频已生成</h3>
              <video controls src={videoUrl} width="100%" type="video/mp4"></video>
              <button
                onClick={handleDownload}
                className="download-button"
              >
                下载MP4视频
              </button>
            </div>
          )}
        </div>
      )}
      
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <p>正在生成视频，请稍候...</p>
            <p className="processing-hint">视频转换可能需要较长时间，请耐心等待</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
